import { useCallback, useEffect, useState } from 'react';
import { Modal, Input, Upload, message, Empty, Spin, Popconfirm, Button, Space, Typography } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { listDashboardAssets, uploadDashboardAsset, deleteDashboardAsset, DashboardAsset } from '../../api/assets';

const { Text } = Typography;

interface AssetLibraryModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}

/** 图片素材库：网格缩略图 + 关键词搜索 + 上传新素材，点击选中回填 URL */
export default function AssetLibraryModal({ open, onClose, onSelect }: AssetLibraryModalProps) {
  const [assets, setAssets] = useState<DashboardAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async (kw?: string) => {
    setLoading(true);
    try {
      setAssets(await listDashboardAssets(kw));
    } catch {
      message.error('获取素材列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // 关键词实时过滤（防抖 300ms）
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => load(keyword), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, open]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await uploadDashboardAsset(file);
      message.success('上传成功');
      await load(keyword);
    } catch (e: any) {
      const detail = e?.response?.data?.msg || e?.response?.data?.message || e?.message || '未知错误';
      message.error(`上传失败: ${detail}`);
    } finally {
      setUploading(false);
    }
    return false; // 阻止 antd Upload 默认行为
  };

  const handleDelete = async (asset: DashboardAsset) => {
    try {
      await deleteDashboardAsset(asset.id);
      message.success('已删除');
      await load(keyword);
    } catch {
      message.error('删除失败');
    }
  };

  return (
    <Modal
      title="图片素材库"
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      destroyOnHidden
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Input
          size="small"
          prefix={<SearchOutlined />}
          placeholder="搜索素材名称（输入即过滤）"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ width: 260 }}
          allowClear
        />
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={(file) => handleUpload(file as File)}
          disabled={uploading}
        >
          <Button size="small" type="primary" icon={<PlusOutlined />} loading={uploading}>
            上传新图片
          </Button>
        </Upload>
      </Space>

      <div style={{ minHeight: 320, maxHeight: 440, overflow: 'auto' }}>
        <Spin spinning={loading}>
          {assets.length === 0 && !loading ? (
            <Empty description="暂无素材，点击右上角上传" />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
              {assets.map((a) => (
                <div
                  key={a.id}
                  style={{
                    position: 'relative',
                    border: '1px solid #333',
                    borderRadius: 6,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    aspectRatio: '1 / 1',
                    background: '#0d0d1a',
                  }}
                  onClick={() => onSelect(a.url)}
                  title={a.name}
                >
                  <img
                    src={a.url}
                    alt={a.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.2'; }}
                  />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2px 6px', fontSize: 11, background: 'rgba(0,0,0,0.6)', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.name}
                  </div>
                  <Popconfirm title="删除该素材？" onConfirm={(e) => { e?.stopPropagation?.(); handleDelete(a); }}>
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      style={{ position: 'absolute', top: 2, right: 2, color: '#fff', background: 'rgba(0,0,0,0.5)' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                </div>
              ))}
            </div>
          )}
        </Spin>
      </div>
      <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
        <InboxOutlined /> 点击素材即可引用到当前属性（背景图/组件图片）；素材按用户隔离存储。
      </div>
    </Modal>
  );
}